
import { useCallback, useState, useMemo } from 'react';
// Fix: Use named imports for ReactFlow hooks from reactflow.
import {
  useNodesState,
  useEdgesState,
  useReactFlow,
} from 'reactflow';
import { defaultProcesses } from '../defaultProcesses';
import { getLayoutedElements } from '../utils/autoLayout';
import { useProcessManagement } from './useProcessManagement';
import { useFileOperations } from './useFileOperations';
import { useNodeOperations } from './useNodeOperations';
import { useEdgeOperations } from './useEdgeOperations';
import { useAutoSave, AutoSavePayload } from './useAutoSave';
import { ProcessDefinition, WorkflowProject } from '../types';
import { getAvailableVariables } from '../utils/variableExtraction';
import { generateWorkflowProject } from '../utils/pssHelper';

interface UseWorkflowEditorProps {
    initialData?: ProcessDefinition[];
    onAutoSave?: (payload: AutoSavePayload) => void;
}

export const useWorkflowEditor = ({ initialData, onAutoSave }: UseWorkflowEditorProps = {}) => {
  // Determine start state from props or default
  const startingProcess = (initialData && initialData.length > 0) 
      ? initialData[0] 
      : defaultProcesses[0];

  // Deep Clone initial state
  const initialProcessClone = JSON.parse(JSON.stringify(startingProcess));
  
  const { fitView, setCenter } = useReactFlow();
  
  const [isLoading, setIsLoading] = useState(false);
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);

  // React Flow State
  const [nodes, setNodes, onNodesChange] = useNodesState(initialProcessClone.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialProcessClone.edges);

  // Sub-hooks
  const {
      processes,
      setProcesses,
      activeProcessId,
      setActiveProcessId,
      handleSelectProcess,
      handleCreateProcess,
      handleDeleteProcess,
      handleUpdateProcess
  } = useProcessManagement(nodes, setNodes, edges, setEdges, initialData);

  const {
      handleSaveProject,
      handleLoadProject
  } = useFileOperations(
      nodes, 
      edges, 
      processes, 
      setProcesses, 
      activeProcessId, 
      setActiveProcessId, 
      setNodes, 
      setEdges, 
      setIsLoading
  );

  // Destructure missing properties from useNodeOperations to expose them to callers
  const {
      selectedNode,
      setSelectedNode,
      isModalOpen,
      setIsModalOpen,
      activeTabId,
      setActiveTabId,
      openModalAtTab,
      onDragOver,
      onDrop,
      onNodeClick,
      onPaneClick,
      onNodeDoubleClick,
      onNodeContextMenu,
      updateNodeData
  } = useNodeOperations(nodes, setNodes, setEdges);

  const {
      onConnect,
      onConnectStart,
      onConnectEnd
  } = useEdgeOperations(nodes, setNodes, setEdges);

  // Auto-Save Hook with callback support
  const autoSaveState = useAutoSave(processes, activeProcessId, nodes, edges, onAutoSave);

  // --- Variable Context Calculation ---
  // Recalculate context whenever selection changes or the graph structure (nodes/edges) changes.
  // Including isModalOpen ensures a fresh calculation right when the user opens the editor.
  const variableContext = useMemo(() => {
      if (!selectedNode) return [];
      return getAvailableVariables(nodes, edges, selectedNode.id);
  }, [selectedNode, nodes, edges, isModalOpen]);

  // --- Auto Layout ---
  const handleAutoLayout = useCallback(() => {
    const layoutedNodes = getLayoutedElements(nodes, edges, 'LR');
    setNodes([...layoutedNodes]);
    setTimeout(() => fitView({ duration: 800 }), 10);
  }, [nodes, edges, setNodes, fitView]);

  // Viewport helper
  const focusNode = useCallback((nodeId: string) => {
      const node = nodes.find(n => n.id === nodeId);
      if (node) {
          setCenter(node.position.x + 90, node.position.y + 32, { zoom: 1.2, duration: 800 });
      }
  }, [nodes, setCenter]);

  // Helper to get current full project state in PSS format (for external saves)
  const getProjectData = useCallback((): WorkflowProject => {
      const currentSnapshot = processes.map(p =>
          p.id === activeProcessId ? { ...p, nodes, edges } : p
      );
      return generateWorkflowProject(currentSnapshot, "Saved Project");
  }, [processes, activeProcessId, nodes, edges]);

  return {
    nodes, setNodes, onNodesChange,
    edges, setEdges, onEdgesChange,
    processes, activeProcessId,
    selectedNode, setSelectedNode,
    variableContext, // Export context
    isModalOpen, setIsModalOpen,
    activeTabId,
    setActiveTabId,
    openModalAtTab,
    isDeployModalOpen, setIsDeployModalOpen,
    handleSelectProcess, handleCreateProcess, handleDeleteProcess, handleUpdateProcess,
    handleSaveProject, handleLoadProject,
    handleAutoLayout,
    onConnect, onConnectStart, onConnectEnd, onDrop, onDragOver,
    onNodeClick, onPaneClick, onNodeDoubleClick, onNodeContextMenu,
    updateNodeData, fitView, focusNode,
    setIsLoading, isLoading, autoSaveState,
    getProjectData
  };
};